/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { mergeStyleSets } from '@fluentui/merge-styles';
import {
    CollapseAllVisibility,
    ColumnActionsMode,
    ConstrainMode,
    DetailsHeader,
    DetailsRow,
    IColumn,
    IDetailsHeaderProps,
    IDetailsRowBaseProps,
    IDetailsRowProps
} from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { IRenderFunction } from '@fluentui/react/lib/Utilities';
import debounce from 'lodash.debounce';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { Table } from 'src/core/components/Table';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { globalMessages } from 'src/core/utils/globalMessages';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';
import { DifferenceCell } from 'src/luis/projects/utterances/components/UtteranceTable/DifferenceCell/DifferenceCell';
import { IntentPageUtteranceRow } from 'src/luis/projects/utterances/components/UtteranceTable/IntentPageUtteranceRow';
import { ScoreCell } from 'src/luis/projects/utterances/components/UtteranceTable/ScoreCell/ScoreCell';
import { UtteranceTableProps } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceTableProps';
import { getNearestRivalIntent } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { ResizeProvider, useResizeTrigger } from 'src/modules/labeling/contexts/ResizeContext';

const messages = defineMessages({
    score: { id: 'utterances.utteranceTable.score', defaultMessage: 'Score' },
    difference: { id: 'utterances.utteranceTable.difference', defaultMessage: 'Difference' },
    nearestIntent: { id: 'utterances.utteranceTable.nearestIntent', defaultMessage: 'Nearest intent' },
    exampleUtterance: { id: 'utterances.utteranceTable.exampleUtterance', defaultMessage: 'Example user input' },
    addNewUtteranceLabel: { id: 'utterances.utteranceTable.addNewUtteranceLabel', defaultMessage: 'Add new utterance input' },
    emptyTablePlaceholder: { id: 'utterances.utteranceTable.emptyTablePlaceholder', defaultMessage: 'There are no utterances found.' },
    inputPlaceHolder: {
        id: 'utterances.utteranceTable.inputPlaceHolder',
        defaultMessage: 'Type an example of what a user might say and hit Enter.'
    },
    announcedLabelingControlsMessage: {
        id: 'utterances.utteranceTable.announcedLabelingControlsMessage',
        defaultMessage: 'Use shift and arrows keys to select tokens and then use tab key to label them from the dropdown menu.'
    }
});

const { getClassName, styleComponent, styleDiv } = getStylistV2('UtteranceTable');

const expandButtonStyles = getClassName('expandButtonStyles', { display: 'none' });

const Row = styleComponent<IDetailsRowBaseProps>(DetailsRow)('Row', theme => ({
    minHeight: theme.minRowHeight
}));

const HeaderWrapper = styleDiv('HeaderWrapper', theme => ({
    borderBottom: theme.headerWrapperBorder,
    padding: theme.headerWrapperInnerSpacing,
    minHeight: theme.headerWrapperMinRowHeight
}));

const utteranceTableTheme = getScopedTheme('UtteranceTable');

const baseInputStyling = {
    minHeight: utteranceTableTheme.addUtteranceInputHeight
};
const addUtteranceInputStyles = (props: ITextFieldStyleProps): Partial<ITextFieldStyles> => ({
    ...((props.focused && {
        fieldGroup: baseInputStyling,
        root: baseInputStyling,
        wrapper: baseInputStyling,
        field: baseInputStyling
    }) || {
        fieldGroup: {
            border: utteranceTableTheme.addUtteranceInputBorder,
            minHeight: utteranceTableTheme.addUtteranceInputHeight
        },
        root: baseInputStyling,
        wrapper: baseInputStyling,
        field: { ...baseInputStyling, border: `1px solid ${utteranceTableTheme.inputBorder}`, borderRadius: '2px' }
    })
});

const MAX_DISPLAYED_UTTERANCES = 20;

/**
 * Represents a table that contains utterances for each intent
 */
const UtteranceTableBase = (props: UtteranceTableProps) => {
    const {
        reAssignableIntents: intents,
        viewOptions,
        isLoading,
        items,
        onDelete,
        onInputSubmit,
        selection,
        onReassignIntent,
        onAddToPatterns,
        isRTL,
        onEdit,
        utteranceIdBeingEdited,
        onSubmitEditInput,
        onDismissEditInput,
        lastTrainingDate,
        isAddAsPatternDisabled,
        onConfirmPrediction,
        labelingStore,
        model,
        sidePaneEntitySelection,
        userRole,
        displayedUserRole,
        ...rest
    } = props;
    const triggerResize = useResizeTrigger();
    const debouncedTriggerSize = React.useCallback(debounce(triggerResize, 1000, { leading: true, trailing: false }), [triggerResize]);

    const [utteranceInput, setUtteranceInput] = React.useState('');
    const { localize } = useLocalization();
    const eventLoggerRef = React.useRef(getEventLogger('UtteranceTableBase'));

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const onUtteranceInputChange = (newUtterance: string) => {
        if (utteranceInput === '') {
            eventLoggerRef.current.start('AddNewUtterance');
        }
        setUtteranceInput(newUtterance);
    };

    const onUtteranceInputSubmit = () => {
        onInputSubmit(utteranceInput);
        setUtteranceInput('');
        eventLoggerRef.current.end('AddNewUtterance');
    };
    /**
     * Fires of rendering utterances table rows.
     */
    const onRenderRow = React.useCallback((rowProps: IDetailsRowProps) => <Row {...rowProps} />, []);

    /**
     * Renders a custom header to display the input field to add new utterance.
     * Due to ICM#207247246: Mitigated the issue by adding the focus zone wrapping
     * the text field, as it trapped the focus of the text field so if the user hit
     * right arrow key after pasting a text it will keep the focus within the text
     * field.
     */
    const onRenderHeader = React.useCallback(
        () => (
            <HeaderWrapper>
                <LuisTextField
                    autoFocus
                    disabled={!canWrite}
                    value={utteranceInput}
                    data-is-focusable="true"
                    onEnterKeyUp={onUtteranceInputSubmit}
                    data-automation-id="utteranceSubmitInput"
                    onChange={(_, newUtterance) => onUtteranceInputChange(newUtterance)}
                    styles={
                        canWrite ? addUtteranceInputStyles : mergeStyleSets({ field: { cursor: 'not-allowed' } }, addUtteranceInputStyles)
                    }
                    placeholder={
                        canWrite ? localize(messages.inputPlaceHolder) : localize(globalMessages.cannotPerformAction, { displayedUserRole })
                    }
                    title={
                        canWrite
                            ? localize(messages.addNewUtteranceLabel)
                            : localize(globalMessages.cannotPerformAction, { displayedUserRole })
                    }
                />
            </HeaderWrapper>
        ),
        [localize, onInputSubmit, setUtteranceInput, utteranceInput]
    );
    /**
     * Renders a custom header for the table to display the input field to add new utterance.
     */
    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = React.useCallback(
        (headerProps: IDetailsHeaderProps) => (
            <Stack>
                <DetailsHeader {...headerProps} />
                {onRenderHeader()}
            </Stack>
        ),
        [localize, onInputSubmit, setUtteranceInput, utteranceInput]
    );

    /**
     * Utterance table columns.
     */
    const columns: IColumn[] = [
        {
            key: 'utterance',
            name: localize(messages.exampleUtterance),
            fieldName: 'utterance',
            minWidth: 100,
            isRowHeader: true,
            isCollapsible: false,
            isMultiline: true,
            onColumnResize: debouncedTriggerSize,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (utteranceObservable: UtteranceObservable) => (
                <IntentPageUtteranceRow
                    model={model}
                    labelingStore={labelingStore}
                    sidePaneEntitySelection={sidePaneEntitySelection}
                    viewOptions={viewOptions}
                    lastTrainingDate={lastTrainingDate}
                    isRTL={isRTL}
                    intents={intents}
                    utteranceObservable={utteranceObservable}
                    onAddToPatterns={onAddToPatterns}
                    onDelete={onDelete}
                    onConfirmPrediction={onConfirmPrediction}
                    onReassignIntent={onReassignIntent}
                    onEdit={onEdit}
                    onDismissEditInput={onDismissEditInput}
                    utteranceIdBeingEdited={utteranceIdBeingEdited}
                    onSubmitEditInput={onSubmitEditInput}
                    isAddAsPatternDisabled={isAddAsPatternDisabled}
                    userRole={userRole}
                />
            )
        },
        {
            key: 'score',
            name: localize(messages.score),
            fieldName: 'score',
            minWidth: 100,
            isCollapsible: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (utteranceObservable: UtteranceObservable) => (
                <ScoreCell utteranceObservable={utteranceObservable} lastTrainingDate={lastTrainingDate} />
            )
        },
        {
            key: 'nearestIntent',
            name: localize(messages.nearestIntent),
            fieldName: 'nearestIntent',
            minWidth: 100,
            isCollapsible: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (utteranceObservable: UtteranceObservable) => {
                const { utteranceWithAnalytics } = utteranceObservable;
                const nearestIntent = getNearestRivalIntent(utteranceWithAnalytics, intents);

                return (
                    <Stack verticalFill verticalAlign="center" horizontal tokens={{ childrenGap: 10 }}>
                        {nearestIntent ? (
                            <>
                                <LuisText>{`${nearestIntent.score.toFixed(2)}`}</LuisText>
                                <LuisTooltip content={`${nearestIntent.intentName}`}>
                                    <TruncatedText>{`${nearestIntent.intentName}`}</TruncatedText>
                                </LuisTooltip>
                            </>
                        ) : (
                            <span>–</span>
                        )}
                    </Stack>
                );
            }
        },
        {
            key: 'difference',
            name: localize(messages.difference),
            fieldName: 'difference',
            minWidth: 100,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (utteranceObservable: UtteranceObservable) => {
                const { utteranceWithAnalytics } = utteranceObservable;

                return <DifferenceCell utterance={utteranceWithAnalytics} intents={intents} />;
            }
        }
    ];

    return (
        <Table<UtteranceObservable>
            getKey={(utterance: UtteranceObservable) => utterance?.id.toString()}
            constrainMode={ConstrainMode.unconstrained}
            respectViewportHeight={false}
            onShouldVirtualize={() => items.length > MAX_DISPLAYED_UTTERANCES}
            items={items}
            selection={selection}
            isLoading={isLoading}
            onRenderRow={onRenderRow}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            ariaLabelForGrid={localize(messages.announcedLabelingControlsMessage)}
            columns={viewOptions.hideIntentDetailedScores ? columns.slice(0, 2) : columns}
            groups={
                items.length > 0
                    ? [
                          {
                              startIndex: 0,
                              name: 'Add utterance',
                              key: 'AddUtteranceHeader',
                              count: items.length ? items.length : 1,
                              isShowingAll: true
                          }
                      ]
                    : []
            }
            groupProps={{
                onRenderHeader,
                collapseAllVisibility: CollapseAllVisibility.hidden,
                headerProps: { expandButtonProps: { className: expandButtonStyles } }
            }}
            onRenderDetailsHeader={items.length === 0 && onRenderDetailsHeader}
            {...rest}
        />
    );
};

/**
 * Wraps the utterance table with ResizeProvider that synchronizes resizing utterance
 * table column with the Labeling LayoutManager.
 */
export const UtteranceTable = React.memo((props: UtteranceTableProps) => (
    <ResizeProvider>
        <UtteranceTableBase {...props} />
    </ResizeProvider>
));

UtteranceTable.displayName = 'UtteranceTable';
